import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSignupAttribution, notifyLocationChange, track } from '@reed/analytics';
import { Alert, Button, Card, CardBody, CardHeader, Input, Radio, Spinner } from '../components/ui';
import {
  BaseProfileFields,
  EMPTY_BASE_PROFILE,
  validateBaseProfile,
  type BaseProfileErrors,
  type BaseProfileState,
} from '../components/onboarding/BaseProfileFields';
import { TermsAcceptanceStep } from '../components/onboarding/TermsAcceptanceStep';
import {
  buildOnboardingAuthorizeURL,
  checkOrganisationExists,
  completeEmployer,
  CURRENT_TERMS_VERSION,
  dashboardURLFor,
  deriveTerritory,
  extractApiErrorCode,
  extractApiErrorMessage,
  getAuthSession,
  putAnonAttribution,
  putAnonSession,
  putAuthSession,
  requestOrganisationAccess,
  searchCharities,
  searchCompanies,
  type CompanySearchResult,
  type EmployerOnboardingBody,
  type OrgType,
} from '../lib/auth-onboarding';
import { runBeforeNavigation } from '../lib/analytics';
import { buildOptionalConsents } from '../lib/consents';
import { isValidEmail } from '../lib/validation';

// ── Anon session ID ───────────────────────────────────────────────────────────
const ANON_SESSION_KEY = 'employer_onboarding_anon_session_id';

function getOrCreateAnonSessionId(): string {
  const existing = localStorage.getItem(ANON_SESSION_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(ANON_SESSION_KEY, id);
  return id;
}

// Refreshes the anonymous session's attribution just before the WorkOS
// redirect (#1458): the visitor may have accepted cookies, or GA may have set
// its cookie, after the company step.
async function refreshAnonAttribution(anonSessionId: string, signal: AbortSignal): Promise<void> {
  const attribution = await getSignupAttribution();
  if (attribution) {
    await putAnonAttribution(anonSessionId, attribution, signal);
  }
}

// ── Wizard step 1: company type + search ─────────────────────────────────────

interface CompanyPickerProps {
  orgType: OrgType;
  onOrgTypeChange: (t: OrgType) => void;
  selected: CompanySearchResult | null;
  onSelect: (r: CompanySearchResult | null) => void;
  error?: string | undefined;
  disabled?: boolean;
}

function CompanyPicker({ orgType, onOrgTypeChange, selected, onSelect, error, disabled }: CompanyPickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CompanySearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestGeneration = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const runSearch = useCallback(
    (q: string, generation: number) => {
      if (generation !== requestGeneration.current) return;
      setSearching(true);
      const fn = orgType === 'charity' ? searchCharities : searchCompanies;
      fn(q)
        .then((r) => {
          if (generation !== requestGeneration.current) return;
          setResults(r);
          setOpen(r.length > 0);
        })
        .catch(() => {
          if (generation !== requestGeneration.current) return;
          setResults([]);
          setOpen(false);
        })
        .finally(() => {
          if (generation === requestGeneration.current) setSearching(false);
        });
    },
    [orgType],
  );

  function clearTimer(): void {
    if (!timer.current) return;
    clearTimeout(timer.current);
    timer.current = null;
  }

  function handleQueryChange(v: string): void {
    setQuery(v);
    clearTimer();
    const generation = ++requestGeneration.current;
    setSearching(false);
    if (v.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(() => {
      timer.current = null;
      runSearch(v, generation);
    }, 350);
  }

  function handleSelect(r: CompanySearchResult): void {
    ++requestGeneration.current;
    clearTimer();
    setSearching(false);
    setQuery(r.legal_name);
    setOpen(false);
    onSelect(r);
  }

  function handleOrgTypeSelection(nextOrgType: OrgType): void {
    if (nextOrgType === orgType) return;
    ++requestGeneration.current;
    clearTimer();
    setQuery('');
    setResults([]);
    setSearching(false);
    setOpen(false);
    onSelect(null);
    onOrgTypeChange(nextOrgType);
  }

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(
    () => () => {
      ++requestGeneration.current;
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-text-primary">Organisation type</legend>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          {(['employer', 'charity'] as OrgType[]).map((t) => (
            <Radio
              key={t}
              name="org_type"
              value={t}
              label={t === 'employer' ? 'Registered company' : 'Registered charity'}
              labelClassName="!text-text-primary"
              size="mobile"
              checked={orgType === t}
              disabled={disabled}
              onChange={() => handleOrgTypeSelection(t)}
            />
          ))}
        </div>
      </fieldset>

      <div ref={containerRef} className="relative">
        <Input
          label={orgType === 'charity' ? 'Charity name' : 'Company name'}
          placeholder={orgType === 'charity' ? 'Search CCEW register...' : 'Search Companies House...'}
          value={selected ? selected.legal_name : query}
          disabled={disabled}
          {...(error ? { errorText: error } : {})}
          {...(searching
            ? {
                icon: (
                  <Spinner
                    size="sm"
                    label={orgType === 'charity' ? 'Searching Charity Commission' : 'Searching Companies House'}
                  />
                ),
              }
            : {})}
          onChange={(e) => {
            if (selected) onSelect(null);
            handleQueryChange(e.target.value);
          }}
          autoComplete="off"
        />
        {open && results.length > 0 && (
          <ul
            aria-label={orgType === 'charity' ? 'Charity search results' : 'Company search results'}
            className="absolute top-full z-10 mt-2 max-h-56 w-full overflow-auto rounded-s border border-border-input bg-surface-primary py-1 shadow-lg"
          >
            {results.map((r) => (
              <li key={r.registration_number}>
                {/* Buttons are focusable + keyboard-activatable so AT users can
                    select a result; onMouseDown-only <li> excluded them. */}
                <button
                  type="button"
                  className="flex w-full flex-col px-3 py-2 text-left hover:bg-surface-secondary focus-visible:bg-surface-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-dark-carnaby-pink"
                  onClick={() => handleSelect(r)}
                >
                  <span className="text-sm font-medium text-text-primary">{r.legal_name}</span>
                  <span className="text-xs text-text-secondary">
                    {r.registration_number}
                    {r.address ? ` · ${r.address}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <div className="rounded-md border border-border-tertiary bg-surface-secondary px-3 py-2 text-sm text-text-primary">
          <span className="font-medium">{selected.legal_name}</span>
          <span className="ml-2 text-xs text-text-secondary">{selected.registration_number}</span>
        </div>
      )}
    </div>
  );
}

// ── Post-WorkOS: personal profile form ───────────────────────────────────────

interface PostAuthFormProps {
  token: string;
}

function PostAuthForm({ token }: PostAuthFormProps): JSX.Element {
  const [step, setStep] = useState<'profile' | 'terms'>('profile');
  const [base, setBase] = useState<BaseProfileState>(EMPTY_BASE_PROFILE);
  const [extras, setExtras] = useState({ roleTitle: '', firstName: '', lastName: '' });
  const [errors, setErrors] = useState<BaseProfileErrors>({});
  const [nameErrors, setNameErrors] = useState<{ firstName?: string | undefined; lastName?: string | undefined }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    deriveTerritory()
      .then((d) => {
        if (cancelled) return;
        setBase((prev) => ({ ...prev, territory: d.territory, territorySource: d.territory_source }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  function updateBase<K extends keyof BaseProfileState>(key: K, next: BaseProfileState[K]): void {
    setBase((prev) => ({ ...prev, [key]: next }));
  }



  function handleContinue(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const v = validateBaseProfile(base);
    setErrors(v);
    // #331: capture the employer's name here so their own name (not the seed
    // account's) shows across the recruiter console. Both are required.
    const ne: { firstName?: string; lastName?: string } = {};
    if (!extras.firstName.trim()) ne.firstName = 'Please enter your first name.';
    if (!extras.lastName.trim()) ne.lastName = 'Please enter your last name.';
    setNameErrors(ne);
    if (Object.keys(v).length > 0 || Object.keys(ne).length > 0) return;
    void track('profile_details_submitted', {});
    setSubmitError(null);
    setStep('terms');
  }

  async function handleSubmit(): Promise<void> {
    const body: EmployerOnboardingBody = {
      territory: base.territory,
      territory_source: base.territorySource,
      phone: base.phone,
      postcode: base.postcode,
      accept_terms_version: CURRENT_TERMS_VERSION,
      first_name: extras.firstName.trim(),
      last_name: extras.lastName.trim(),
      // Employers capture no WhatsApp/mobile consent (frame E-3).
      optional_consents: buildOptionalConsents({
        audience: 'employer',
        marketingOptOut: base.marketingOptOut,
      }),
      ...(base.county.trim() ? { county: base.county.trim() } : {}),
      ...(extras.roleTitle.trim() ? { role_title: extras.roleTitle.trim() } : {}),
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      // The final capture (#1458). It also covers WorkOS-hosted signups, which
      // never wrote an anonymous session. Bounded, so a slow or blocked tag
      // never holds up the signup.
      const attribution = await getSignupAttribution();
      const res = await completeEmployer(attribution ? { ...body, attribution } : body, token);
      // Clear anon session now that org is created
      localStorage.removeItem(ANON_SESSION_KEY);
      window.location.replace(`${dashboardURLFor('employer')}/auth/callback#token=${res.token}`);
    } catch (err) {
      setSubmitError(extractApiErrorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 xl:py-12">
      {step === 'profile' ? (
        <Card>
          <CardHeader
            title={<h1 className="whitespace-normal">Almost there — a few more details</h1>}
            trailing={<span className="whitespace-nowrap text-sm text-text-secondary">Step 1 of 2</span>}
            className="flex-col md:flex-row"
          />
          <CardBody>
            <p className="mb-6 text-sm text-text-secondary">
              Your company details have been submitted for verification. Add your contact details to complete sign-up.
            </p>
            <form className="flex flex-col gap-5" onSubmit={handleContinue} noValidate>
              <div className="flex flex-col gap-5 md:flex-row">
                <Input
                  label="First name"
                  autoComplete="given-name"
                  required
                  aria-invalid={!!nameErrors.firstName}
                  value={extras.firstName}
                  disabled={submitting}
                  {...(nameErrors.firstName ? { errorText: nameErrors.firstName } : {})}
                  onChange={(e) => {
                    if (nameErrors.firstName) setNameErrors((n) => ({ ...n, firstName: undefined }));
                    setExtras((x) => ({ ...x, firstName: e.target.value }));
                  }}
                />
                <Input
                  label="Last name"
                  autoComplete="family-name"
                  required
                  aria-invalid={!!nameErrors.lastName}
                  value={extras.lastName}
                  disabled={submitting}
                  {...(nameErrors.lastName ? { errorText: nameErrors.lastName } : {})}
                  onChange={(e) => {
                    if (nameErrors.lastName) setNameErrors((n) => ({ ...n, lastName: undefined }));
                    setExtras((x) => ({ ...x, lastName: e.target.value }));
                  }}
                />
              </div>
              <Input
                label="Your role (optional)"
                placeholder="e.g. Head of Talent"
                value={extras.roleTitle}
                disabled={submitting}
                onChange={(e) => setExtras((x) => ({ ...x, roleTitle: e.target.value }))}
              />
              <BaseProfileFields
                value={base}
                errors={errors}
                audience="employer"
                disabled={submitting}
                onChange={updateBase}
              />
              <Button type="submit" className="w-full">
                Continue
              </Button>
            </form>
          </CardBody>
        </Card>
      ) : (
        <TermsAcceptanceStep
          audience="employer"
          accepted={base.acceptedTerms}
          marketingOptOut={base.marketingOptOut}
          onToggleAccepted={(next) => updateBase('acceptedTerms', next)}
          onToggleMarketingOptOut={(next) => updateBase('marketingOptOut', next)}
          onBack={() => setStep('profile')}
          onSubmit={() => void handleSubmit()}
          submitting={submitting}
          submitError={submitError}
        />
      )}
    </div>
  );
}

// ── Blocked-banner "Request access" (scope-b double opt-in) ───────────────────

interface RequestAccessFormProps {
  registrationNumber: string;
  emailHint?: string;
}

// RequestAccessForm turns the org-exists dead-end into an action: the blocked
// user submits their name + work email, and a confirm link is emailed to *them*
// (double opt-in). Only on confirm are the org's admins notified — so this
// component's success state is deliberately "check your email", never "request
// sent". The POST response is constant-shape and reveals nothing about the org.
function RequestAccessForm({ registrationNumber, emailHint }: RequestAccessFormProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState(emailHint ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || !trimmedEmail) {
      setError('Please enter your name and work email.');
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError('Please enter a valid work email address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await requestOrganisationAccess({
        registration_number: registrationNumber,
        name: trimmedName,
        email: trimmedEmail,
      });
      setDone(true);
    } catch (err) {
      setError(extractApiErrorMessage(err));
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Alert
        variant="success"
        message="Check your email to confirm your request. Once you confirm, your organisation's admins will be notified and can send you an invite."
      />
    );
  }

  if (!open) {
    return (
      <div>
        <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
          Request access
        </Button>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)} noValidate>
      <Input
        label="Your name"
        value={name}
        disabled={submitting}
        autoComplete="name"
        onChange={(e) => setName(e.target.value)}
      />
      <Input
        label="Work email"
        type="email"
        value={email}
        disabled={submitting}
        autoComplete="email"
        onChange={(e) => setEmail(e.target.value)}
      />
      {error && <Alert variant="error" message={error} />}
      <div className="flex flex-col-reverse gap-3 md:flex-row">
        <Button
          type="button"
          variant="secondary"
          className="flex-1"
          disabled={submitting}
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
        <Button type="submit" className="flex-1" disabled={submitting}>
          {submitting ? <Spinner size="sm" /> : null}
          Send request
        </Button>
      </div>
    </form>
  );
}

// ── Pre-WorkOS wizard ─────────────────────────────────────────────────────────

export default function OnboardingEmployerPage(): JSX.Element {
  const navigate = useNavigate();
  // Capture the post-WorkOS token once, then scrub the fragment so the bearer
  // capability never lingers in the address bar / history / referrer.
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');

  useEffect(() => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
      // replaceState emits no router event; analytics waits for a clean URL.
      notifyLocationChange();
    }
  }, []);

  // If we have a post-WorkOS token, resolve whether the org was captured before
  // WorkOS; if not (e.g. a WorkOS-hosted "sign up"), collect it now so every
  // employer completes the SAME Companies House / CCEW journey.
  if (token) {
    return <EmployerTokenMode token={token} />;
  }

  return <EmployerWizard navigate={navigate} />;
}

// EmployerTokenMode guarantees the full employer journey after WorkOS. When the
// company/charity details were captured before WorkOS (our landing wizard) the
// authenticated session already holds them and we go straight to the profile
// step. Otherwise we collect + verify the org here first, so no employer can
// skip Companies House / CCEW — regardless of how they signed up.
function EmployerTokenMode({ token }: { token: string }): JSX.Element {
  const [phase, setPhase] = useState<'loading' | 'company' | 'profile'>('loading');
  const [orgType, setOrgType] = useState<OrgType>('employer');
  const [selected, setSelected] = useState<CompanySearchResult | null>(null);
  const [companyError, setCompanyError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Set when putAuthSession reports the org is already registered — turns the
  // otherwise-terminal 409 into an actionable request-access path.
  const [duplicateReg, setDuplicateReg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAuthSession(token)
      .then((sess) => {
        if (cancelled) return;
        setPhase(sess && sess.registration_number ? 'profile' : 'company');
      })
      .catch(() => {
        if (!cancelled) setPhase('company');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function saveCompany(): Promise<void> {
    if (!selected) {
      setCompanyError('Please search for and select your organisation');
      return;
    }
    setCompanyError(undefined);
    setSaving(true);
    setSaveError(null);
    try {
      const attribution = await getSignupAttribution();
      await putAuthSession(token, 'company_details', {
        org_type: orgType,
        registration_number: selected.registration_number,
        legal_name: selected.legal_name,
        country_code: 'GB',
        ...(attribution ? { attribution } : {}),
      });
      void track('company_selected', { org_type: orgType, flow: 'post_auth' });
      setPhase('profile');
    } catch (err) {
      if (extractApiErrorCode(err) === 'ORGANISATION_ALREADY_EXISTS') {
        setDuplicateReg(selected.registration_number);
      } else {
        setSaveError(extractApiErrorMessage(err, 'Could not save your details. Please try again.'));
      }
    } finally {
      setSaving(false);
    }
  }

  if (phase === 'loading') {
    return (
      <div className="mx-auto flex w-full max-w-md px-4 py-8 sm:px-6 sm:py-10 xl:py-12">
        <Card variant="elevated" className="w-full text-center">
          <div className="flex flex-col items-center gap-3 py-4">
            <Spinner label="Loading organisation details" />
            <p className="text-sm text-text-secondary">Loading your organisation details…</p>
          </div>
        </Card>
      </div>
    );
  }
  if (phase === 'profile') {
    return <PostAuthForm token={token} />;
  }
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 xl:py-12">
      <Card>
        <CardHeader
          title={<h1 className="whitespace-normal">Tell us about your organisation</h1>}
          trailing={<span className="whitespace-nowrap text-sm text-text-secondary">Step 1 of 2</span>}
          className="flex-col md:flex-row"
        />
        <CardBody>
          <p className="mb-6 text-sm text-text-secondary">
            Find your company or charity so we can verify it and set up your account.
          </p>
          <div className="flex flex-col gap-5">
            <CompanyPicker
              orgType={orgType}
              onOrgTypeChange={setOrgType}
              selected={selected}
              onSelect={(r) => {
                setDuplicateReg(null);
                setSaveError(null);
                setSelected(r);
              }}
              error={companyError}
              disabled={saving}
            />
            {duplicateReg ? (
              <>
                <Alert
                  variant="warning"
                  message="This organisation is already registered. Request access below and an existing administrator will be notified, or ask a colleague to invite you."
                />
                <RequestAccessForm registrationNumber={duplicateReg} />
              </>
            ) : (
              <>
                {saveError && <Alert variant="error" message={saveError} />}
                <Button className="w-full" disabled={saving} onClick={() => void saveCompany()}>
                  Continue
                </Button>
              </>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

interface EmployerWizardProps {
  navigate: ReturnType<typeof useNavigate>;
}

function EmployerWizard({ navigate: _navigate }: EmployerWizardProps): JSX.Element {
  const [searchParams] = useSearchParams();
  // Capture the email hint once, then strip it from the query string so the
  // requester's email PII is not retained in the address bar / history / referrer.
  const [emailHint] = useState(() => searchParams.get('email') ?? '');
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('email')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('email');
      history.replaceState(null, '', url.pathname + url.search + url.hash);
      notifyLocationChange();
    }
  }, []);
  const [step, setStep] = useState<1 | 2>(1);
  const [orgType, setOrgType] = useState<OrgType>('employer');
  const [selected, setSelected] = useState<CompanySearchResult | null>(null);
  const [orgExists, setOrgExists] = useState(false);
  const [checkingOrg, setCheckingOrg] = useState(false);
  const checkSeq = useRef(0);

  function handleOrgSelect(r: CompanySearchResult | null): void {
    setSelected(r);
    setCompanyError(undefined);
    setOrgExists(false);
    const seq = ++checkSeq.current;
    if (!r) {
      setCheckingOrg(false);
      return;
    }
    setCheckingOrg(true);
    checkOrganisationExists(r.registration_number)
      .then((exists) => { if (seq === checkSeq.current) setOrgExists(exists); })
      .catch(() => { if (seq === checkSeq.current) setOrgExists(false); }) // fail-open
      .finally(() => { if (seq === checkSeq.current) setCheckingOrg(false); });
  }
  const [companyError, setCompanyError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const anonSessionId = useRef(getOrCreateAnonSessionId());

  async function handleStep1Continue(): Promise<void> {
    if (!selected) {
      setCompanyError('Please search for and select your organisation');
      return;
    }
    setCompanyError(undefined);
    setSaving(true);
    setSaveError(null);
    try {
      const attribution = await getSignupAttribution();
      await putAnonSession(anonSessionId.current, 'company_details', {
        org_type: orgType,
        registration_number: selected.registration_number,
        legal_name: selected.legal_name,
        // Companies House and CCEW are both GB-only registers.
        country_code: 'GB',
        ...(attribution ? { attribution } : {}),
      });
      void track('company_selected', { org_type: orgType, flow: 'pre_auth' });
      setStep(2);
    } catch (err) {
      setSaveError(extractApiErrorMessage(err, 'Could not save your details. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleStartAuth(): Promise<void> {
    setRedirecting(true);
    // Best-effort and bounded (PRE_NAVIGATION_BUDGET_MS): neither a slow
    // attribution write nor a blocked tag may hold up the redirect.
    await runBeforeNavigation((signal) =>
      Promise.all([refreshAnonAttribution(anonSessionId.current, signal), track('account_create_click', {})]),
    );
    window.location.assign(buildOnboardingAuthorizeURL(anonSessionId.current, emailHint || undefined));
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 xl:py-12">
      <Card>
        <CardHeader
          title={<h1 className="whitespace-normal">Set up your hiring workspace</h1>}
          trailing={<span className="whitespace-nowrap text-sm text-text-secondary">Step {step} of 2</span>}
          className="flex-col md:flex-row"
        />
        <CardBody>
          {step === 1 && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-text-secondary">
                Search the Companies House or Charity Commission register to verify your organisation.
                This protects your team and job seekers.
              </p>
              <CompanyPicker
                orgType={orgType}
                onOrgTypeChange={setOrgType}
                selected={selected}
                onSelect={handleOrgSelect}
                error={companyError}
                disabled={saving}
              />
              {orgExists && selected && (
                <div className="flex flex-col gap-4">
                  <Alert
                    variant="warning"
                    message={`${selected.legal_name} already has an account on Reed.ai. Ask your organisation's administrator to invite you, or request access below.`}
                  />
                  <RequestAccessForm
                    registrationNumber={selected.registration_number}
                    {...(emailHint ? { emailHint } : {})}
                  />
                </div>
              )}
              {saveError && <Alert variant="error" message={saveError} />}
              <Button
                type="button"
                className="w-full"
                disabled={saving || checkingOrg || orgExists}
                onClick={() => void handleStep1Continue()}
              >
                {saving ? <Spinner size="sm" /> : null}
                Continue
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-6">
              <div className="rounded-md border border-border-tertiary bg-surface-secondary px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">Selected organisation</p>
                <p className="mt-1 text-sm font-semibold text-text-primary">{selected?.legal_name}</p>
                <p className="text-xs text-text-secondary">{selected?.registration_number}</p>
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-sm text-text-secondary">
                  Next, create your account. You'll be taken to our secure sign-in page
                  — your company details are already saved.
                </p>
                <p className="text-xs text-text-secondary">
                  Your organisation will be verified by our team before you can post jobs.
                  This usually takes 1–2 business days.
                </p>
              </div>
              <div className="flex flex-col-reverse gap-3 md:flex-row">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  disabled={redirecting}
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  disabled={redirecting}
                  onClick={() => void handleStartAuth()}
                >
                  {redirecting ? <Spinner size="sm" /> : null}
                  Create your account
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
